import React, { ReactElement } from "react";
import denialError from "assets/svg/denial-error.svg";
import styles from "./Error.module.css";
import { Button } from "@justworkshr/milo-components";
import { getAppType, getCWRedirectUrl } from "utils/helper";

export enum EErrorType {
  PageNotFound = "PAGE_NOT_FOUND",
  Unexpected = "UNEXPECTED",
  NotEligibleForCoverage = "NOT_ELIGIBLE_FOR_COVERAGE",
  Custom = "CUSTOM",
}

interface IErrorProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  type?: EErrorType;
}

const Error = (props: IErrorProps): ReactElement => {
  const { type = "UNEXPECTED" } = props;
  let { title, description } = props;

  const isUpsellApplication = getAppType() === "HEALTH_INSURANCE_UPSELL";
  let showBackButton = false;
  const backButtonText = isUpsellApplication
    ? "Back to Benefits"
    : "Back to dashboard";
  const backButtonLink = isUpsellApplication
    ? getCWRedirectUrl("/benefits/offer")
    : getCWRedirectUrl("/onboarding_checklist");

  if (type === EErrorType.PageNotFound) {
    title = "We can’t find that page";
    description = "It might’ve taken PTO. Head back and try again.";
    showBackButton = true;
  }

  if (type === EErrorType.Unexpected) {
    title = "Something went wrong";
    description = (
      <>
        That page might’ve taken PTO. Try refreshing the page. If it still
        doesn't work give us a ring at{" "}
        <span className={styles.phoneNumber}>(888) 534-1711.</span>
      </>
    );
    showBackButton = false;
  }

  if (type === EErrorType.NotEligibleForCoverage) {
    title = "Need to update your application?";
    description = !isUpsellApplication ? (
      <>
        If the number of employees on COBRA has decreased or the total number of
        employees has increased email us at{" "}
        <a
          href="mailto:enrollment@justworks.com"
          target="_blank"
          rel="noreferrer"
        >
          enrollment@justworks.com
        </a>{" "}
        to update your application.
      </>
    ) : (
      <>
        If the number of employees on COBRA has decreased or the total number of
        employees has increased email us at{" "}
        <a
          href="mailto: support@justworks.com"
          target="_blank"
          rel="noreferrer"
        >
          support@justworks.com
        </a>{" "}
        to update your application.
      </>
    );
    showBackButton = true;
  }

  return (
    <div className={styles.errorContainer} data-testid="error-component">
      <img src={denialError} alt="justworks logo" />

      <div className={styles.information}>
        <div className={styles.title}>{title}</div>

        <div className={styles.description}>{description}</div>

        {showBackButton && (
          <Button
            className={styles.backToDashboard}
            color="neutral"
            as="a"
            href={backButtonLink}
          >
            {backButtonText}
          </Button>
        )}
      </div>
    </div>
  );
};

export default Error;
